import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gql } from 'graphql-request';

import { Issue, getImagesFromIssue, Article } from '../constants';
import { AppContext } from '../App.context';
import config from '../config';
import { useQuery } from '@tanstack/react-query';

/*
	Redirect to base url if the issue is not found
*/

export function useIssueNotFound(issue?: Issue | null) {
	const navigate = useNavigate();
	const { baseUrl } = useContext(AppContext);
	useEffect(() => {
		if (issue === null) {
			navigate(baseUrl, { replace: true });
		}
	}, [issue]);
}

export function useIssues(): GetIssuesResponse {
	const { graphQLClient, parseGraphQLClientError } = useContext(AppContext);
	const cacheTime = 1000 * 60 * (config.app?.cache?.ttl?.getAllIssues || 60);
	const { data, isPending, error } = useQuery<IssuesQuery>({
		queryKey: ['issues'],
		queryFn: async () => graphQLClient.request(issuesQueryDocument),
		staleTime: cacheTime,
		refetchInterval: cacheTime,
		gcTime: cacheTime,
	});

	useDownloadInBackground(data?.issues);

	return {
		issues: data?.issues ? data?.issues.sort(byReleaseDate) : [],
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

export interface GetIssuesResponse {
	issues: Array<Issue>;
	isLoading: boolean;
	error?: Error | null;
}

interface IssuesQuery {
	issues: Issue[];
}

export const issuesQueryDocument = gql`
	query Issues {
		issues {
			id
			name
			description
			cover
			externalUrl
			price
			index
			slug
			currency
			data
			updated
			created
			isPurchased
			releaseDate
		}
	}
`;

export function useDownloadInBackground(issues?: Array<Issue>) {
	const { sw } = useContext(AppContext);
	useEffect(() => {
		if (!sw) return;
		if (!issues) return;
		if (!issues.length) return;
		const images: Set<string> = new Set();
		getImagesFromIssue(images, issues);
		if (images.size) {
			sw.postMessage({
				type: 'DOWNLOAD_BACKGROUND_IMAGES',
				payload: {
					images: [...images],
				},
			});
		}
	}, [sw, issues]);
}

export function useArticles(args?: ArticleArgs): GetArticlesResponse {
	const { graphQLClient, parseGraphQLClientError } = useContext(AppContext);
	const isFeatured = !!args?.isFeatured;
	const cacheTime =
		1000 * 60 * (config.app?.cache?.ttl?.getFeatureArticles || 60);

	const queryKey: string[] = ['articles'];
	if (isFeatured) {
		queryKey.push('featured');
	}

	const { isPending, error, data } = useQuery<ArticlesQuery>({
		queryKey,
		queryFn: async () =>
			graphQLClient.request(articlesQueryDocument, {
				isFeatured,
			}),
		staleTime: cacheTime,
		refetchInterval: cacheTime,
		gcTime: cacheTime,
	});

	return {
		articles: data?.articles ? data.articles : [],
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

export interface GetArticlesResponse {
	articles: Article[];
	isLoading: boolean;
	error?: Error | null;
}

interface ArticlesQuery {
	articles: Article[];
}

export const articlesQueryDocument = gql`
	query Articles($isFeatured: Boolean) {
		articles(isFeatured: $isFeatured) {
			id
			name
			IssueID
			slug
			thumbnail
			featureImage
			index
			isFeatured
			created
			updated
		}
	}
`;

export function byReleaseDate(a: Issue, b: Issue) {
	if (a.releaseDate && b.releaseDate) {
		return (
			new Date(`${b.releaseDate}`).getTime() -
			new Date(`${a.releaseDate}`).getTime()
		);
	}
	return (
		new Date(`${b.updated}`).getTime() - new Date(`${a.updated}`).getTime()
	);
}

interface ArticleArgs {
	isFeatured?: boolean;
}
